import React, { useEffect, useState, useRef } from "react";
import { Button, Modal, Form, Dropdown, Spinner } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import { getAllIngredientsByLocalId } from "../../../../../../../../crud/menu_api/ingredients";
import { getAllUnidadesMedida } from "../../../../../../../../crud/configuration_api/unidades_medida";
import { getAllProvidersByLocalId } from "../../../../../../../../crud/management_api/providers";
import { updateIngredientPurchaseRecord } from "../../../../../../../../crud/management_api/ingredient_purchases";
import { getAllBrandsByLocalId } from "../../../../../../../../crud/menu_api/brands";
import { TaxType, TAX_TYPE_OPTIONS, ExpenseCategoryType, IngredientPurchaseRecord } from "../../../../types";
import { toast } from "react-toastify";
import "./EditPurchase.scss";

interface Ingredient {
  ingredient_id: number;
  ingredient_name: string;
}

interface Provider {
  provider_id: number;
  name: string;
}

interface Unit {
  unidad_medida_id: number;
  name: string;
}

interface Brand {
  brand_id: number;
  brand_name: string;
}

interface EditPurchaseProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onUpdated: () => void;
  purchase: IngredientPurchaseRecord | null;
  categories: ExpenseCategoryType[];
}

const EditPurchase: React.FC<EditPurchaseProps> = ({
  show,
  setShow,
  onUpdated,
  purchase,
  categories,
}) => {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);

  // Dropdown states
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [showIngredientMenu, setShowIngredientMenu] = useState(false);
  const ingredientInputRef = useRef<HTMLInputElement>(null);

  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [providerSearch, setProviderSearch] = useState("");
  const [showProviderMenu, setShowProviderMenu] = useState(false);
  const providerInputRef = useRef<HTMLInputElement>(null);

  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [showUnitMenu, setShowUnitMenu] = useState(false);

  // Brand
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [brandSearch, setBrandSearch] = useState("");
  const [showBrandMenu, setShowBrandMenu] = useState(false);
  const brandInputRef = useRef<HTMLInputElement>(null);

  // Tax
  const [taxType, setTaxType] = useState<TaxType | null>(null);
  const [ivaIncluded, setIvaIncluded] = useState(false);

  // Category
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategoryType | null>(null);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");

  // Date
  const [purchaseDate, setPurchaseDate] = useState<string>('');

  // Notes
  const [notes, setNotes] = useState<string>('');

  // Quantity & Total fields
  const [quantity, setQuantity] = useState<string>("");
  const [totalNeto, setTotalNeto] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true);
  const quantityInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (show) {
      const user = JSON.parse(localStorage.getItem("user") ?? "{}");
      const localId = user.local_id ? Number(user.local_id) : 0;
      setIsLoadingDropdowns(true);
      Promise.all([
        fetchIngredients(localId),
        fetchProviders(localId),
        fetchUnits(),
        fetchBrands(localId),
      ]).finally(() => setIsLoadingDropdowns(false));
      // Pre-fill fields from purchase
      if (purchase) {
        setSelectedIngredient(null);
        setIngredientSearch("");
        setSelectedProvider(null);
        setProviderSearch("");
        setSelectedUnit(null);
        setSelectedBrand(null);
        setBrandSearch("");
        setQuantity(purchase.quantity.toString());
        setTotalNeto(purchase.total_neto.toString());
        setTaxType(purchase.tax_type || null);
        setIvaIncluded(purchase.tax_type === 'deducible_iva' && purchase.total_with_tax != null);
        setPurchaseDate(purchase.purchase_date || '');
        setNotes(purchase.notes || '');
        // Match category from prop
        const cat = categories.find(c => c.expense_category_id === purchase.expense_category_id) || null;
        setSelectedCategory(cat);
        setCategorySearch("");
      }
    }
  }, [show, purchase]);

  useEffect(() => {
    if (purchase && ingredients.length > 0) {
      const ing = ingredients.find(i => i.ingredient_id === purchase.ingredient_id) || null;
      setSelectedIngredient(ing);
    }
    if (purchase && providers.length > 0) {
      const prov = providers.find(p => p.provider_id === purchase.provider_id) || null;
      setSelectedProvider(prov);
    }
    if (purchase && units.length > 0) {
      const unit = units.find(u => u.unidad_medida_id === purchase.unidad_medida_id) || null;
      setSelectedUnit(unit);
    }
    if (purchase && brands.length > 0) {
      const brand = brands.find(b => b.brand_id === purchase.brand_id) || null;
      setSelectedBrand(brand);
    }
  }, [ingredients, providers, units, brands, purchase]);

  const fetchIngredients = async (local_id: number) => {
    try {
      const response = await getAllIngredientsByLocalId(local_id);
      setIngredients(response.data);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
    }
  };

  const fetchProviders = async (local_id: number) => {
    try {
      const response = await getAllProvidersByLocalId(local_id);
      setProviders(response.data);
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await getAllUnidadesMedida();
      setUnits(response.data);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  const fetchBrands = async (local_id: number) => {
    try {
      const response: any = await getAllBrandsByLocalId(local_id);
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const filteredIngredients = ingredients.filter((ing) =>
    ing.ingredient_name.toLowerCase().includes(ingredientSearch.toLowerCase())
  );

  const filteredProviders = providers.filter((prov) =>
    prov.name.toLowerCase().includes(providerSearch.toLowerCase())
  );

  const filteredBrands = brands.filter((brand) =>
    brand.brand_name.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const filteredCategories = categories.filter((cat) =>
    cat.category_name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const handleUpdate = async () => {
    if (
      !selectedIngredient ||
      !selectedUnit ||
      !quantity ||
      !totalNeto ||
      !purchase
    ) {
      toast.warn("Por favor, complete todos los campos obligatorios.");
      return;
    }

    if (
      typeof purchase.ingredient_purchase_id !== 'number' ||
      typeof selectedIngredient.ingredient_id !== 'number' ||
      typeof selectedUnit.unidad_medida_id !== 'number'
    ) {
      toast.error("Error: Faltan datos requeridos para la edición.");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") ?? "{}");
    const localId = user.local_id ? Number(user.local_id) : 0;

    const payload: any = {
      local_id: localId,
      ingredient_id: selectedIngredient.ingredient_id,
      quantity: parseFloat(quantity),
      total_neto: parseFloat(totalNeto),
      unidad_medida_id: selectedUnit.unidad_medida_id,
      provider_id: selectedProvider?.provider_id || null,
      brand_id: selectedBrand?.brand_id || null,
      tax_type: taxType || null,
      total_with_tax: (taxType === 'deducible_iva' && ivaIncluded) ? parseFloat(totalNeto) : undefined,
      expense_category_id: selectedCategory?.expense_category_id || null,
      purchase_date: purchaseDate || null,
      notes: notes.trim() || null,
      user_id: user.user_id || null,
    };

    setIsLoading(true);
    try {
      await updateIngredientPurchaseRecord(purchase.ingredient_purchase_id, payload);
      toast.success("Compra editada correctamente.");
      onUpdated();
      setShow(false);
    } catch (error) {
      console.error("Error updating ingredient purchase:", error);
      toast.error("Ocurrió un error al actualizar la compra.");
    } finally {
      setIsLoading(false);
    }
  };

  const changeQuantityBy = (deltaSteps: number) => {
    const current = quantity === '' ? 0 : parseFloat(quantity);
    const stepStr = quantityInputRef.current?.getAttribute('step') || quantityInputRef.current?.step || '1';
    const step = parseFloat(String(stepStr)) || 1;
    const decimals = (step.toString().split('.')[1] || '').length;
    let next = current + deltaSteps * step;
    if (next < 0) next = 0;
    setQuantity(next === 0 ? '' : String(Number(next.toFixed(decimals))));
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} centered className="edit-purchase-modal">
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>Editar Compra</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={() => setShow(false)}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Ingredient (searchable dropdown) */}
          <div className="form-field">
            <div className="field-label required">Producto</div>
            <Dropdown
              show={showIngredientMenu}
              onToggle={(isOpen) => {
                setShowIngredientMenu(isOpen);
                if (isOpen && ingredientInputRef.current) {
                  setTimeout(() => ingredientInputRef.current?.focus(), 100);
                }
              }}
            >
              <Dropdown.Toggle variant="none" className="dropdown-toggle">
                {selectedIngredient ? selectedIngredient.ingredient_name : "Seleccione un producto..."}
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu">
                <div className="dropdown-search">
                  <input
                    ref={ingredientInputRef}
                    type="text"
                    placeholder="Buscar producto..."
                    value={ingredientSearch}
                    onChange={(e) => setIngredientSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {isLoadingDropdowns ? (
                  <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando productos...</Dropdown.Item>
                ) : filteredIngredients.length === 0 ? (
                  <Dropdown.Item disabled>No se encontraron productos</Dropdown.Item>
                ) : (
                  filteredIngredients.map((ing) => (
                    <Dropdown.Item
                      key={ing.ingredient_id}
                      onClick={() => {
                        setSelectedIngredient(ing);
                        setIngredientSearch("");
                        setShowIngredientMenu(false);
                      }}
                    >
                      {ing.ingredient_name}
                    </Dropdown.Item>
                  ))
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Provider + Brand (two-column row) */}
          <div className="two-column-row">
          <div className="form-field">
            <div className="field-label">Proveedor</div>
            <Dropdown
              show={showProviderMenu}
              onToggle={(isOpen) => {
                setShowProviderMenu(isOpen);
                if (isOpen && providerInputRef.current) {
                  setTimeout(() => providerInputRef.current?.focus(), 100);
                }
              }}
            >
              <Dropdown.Toggle variant="none" className="dropdown-toggle">
                {selectedProvider ? selectedProvider.name : "Seleccione un proveedor..."}
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu">
                <div className="dropdown-search">
                  <input
                    ref={providerInputRef}
                    type="text"
                    placeholder="Buscar proveedor..."
                    value={providerSearch}
                    onChange={(e) => setProviderSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedProvider(null);
                    setProviderSearch("");
                    setShowProviderMenu(false);
                  }}
                  style={{ opacity: 0.5, fontStyle: 'italic' }}
                >
                  Ninguno
                </Dropdown.Item>
                {isLoadingDropdowns ? (
                  <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando proveedores...</Dropdown.Item>
                ) : filteredProviders.length === 0 ? (
                  <Dropdown.Item disabled>No se encontraron proveedores</Dropdown.Item>
                ) : (
                  filteredProviders.map((prov) => (
                    <Dropdown.Item
                      key={prov.provider_id}
                      onClick={() => {
                        setSelectedProvider(prov);
                        setProviderSearch("");
                        setShowProviderMenu(false);
                      }}
                    >
                      {prov.name}
                    </Dropdown.Item>
                  ))
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Quantity + Unit of Measure (Horizontal) */}
          <div className="horizontal-field-group">
            <div className="field-column quantity-column">
              <div className="field-label required">Cantidad</div>
              <div className="selection-input-container">
                <Form.Control
                  type="number"
                  step="0.1"
                  min="0"
                  className="selection-input"
                  ref={(el: HTMLInputElement | null) => (quantityInputRef.current = el)}
                  placeholder="Ingrese la cantidad"
                  value={quantity}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || parseFloat(val) >= 0) {
                      setQuantity(val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                />
                <div className="selection-arrows">
                  <button type="button" className="arrow-btn arrow-up" onClick={() => changeQuantityBy(1)}>▲</button>
                  <button type="button" className="arrow-btn arrow-down" onClick={() => changeQuantityBy(-1)}>▼</button>
                </div>
              </div>
            </div>

            <div className="field-column unit-column">
              <div className="field-label required">Unidad</div>
              <Dropdown
                show={showUnitMenu}
                onToggle={(isOpen) => setShowUnitMenu(isOpen)}
              >
                <Dropdown.Toggle variant="none" className="dropdown-toggle">
                  {selectedUnit ? selectedUnit.name : "Seleccione..."}
                </Dropdown.Toggle>
                <Dropdown.Menu className="dropdown-menu">
                  {isLoadingDropdowns ? (
                    <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando unidades...</Dropdown.Item>
                  ) : units.length === 0 ? (
                    <Dropdown.Item disabled>No hay unidades disponibles</Dropdown.Item>
                  ) : (
                    units.map((unit) => (
                      <Dropdown.Item
                        key={unit.unidad_medida_id}
                        onClick={() => {
                          setSelectedUnit(unit);
                          setShowUnitMenu(false);
                        }}
                      >
                        {unit.name}
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Total Neto - with dollar prefix */}
          <div className="form-field">
            <div className="field-label required">
              {taxType === 'deducible_iva' && ivaIncluded ? 'Total con IVA' : 'Total Neto'}
            </div>
            <div className="money-input-group">
              <span className="money-prefix">$</span>
              <Form.Control
              type="number"
              step="0.01"
              min="0"
              className="selection-input modern-input"
              placeholder="Ingrese el total neto"
              value={totalNeto}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || parseFloat(val) >= 0) {
                  setTotalNeto(val);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "-" || e.key === "e") {
                  e.preventDefault();
                }
              }}
            />
            </div>
          </div>

          {/* Tax Type Selector */}
          <div className="form-field tax-type-field">
            <div className="field-label">Tipo de Comprobante</div>
            <div className="tax-type-selector">
              {TAX_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`tax-type-option ${taxType === opt.value ? 'selected' : ''} ${opt.value}`}
                  onClick={() => {
                    setTaxType(taxType === opt.value ? null : opt.value);
                    if (opt.value !== 'deducible_iva') setIvaIncluded(false);
                  }}
                >
                  <span className="tax-option-label">{opt.label}</span>
                  <span className="tax-option-desc">{opt.description}</span>
                </button>
              ))}
            </div>
            {taxType === 'deducible_iva' && (
              <label className="iva-toggle">
                <input
                  type="checkbox"
                  checked={ivaIncluded}
                  onChange={(e) => setIvaIncluded(e.target.checked)}
                />
                <span>El total ingresado ya incluye IVA</span>
              </label>
            )}
          </div>

          {/* Brand (searchable dropdown, optional) */}
          <div className="form-field brand-field">
            <div className="field-label">Marca</div>
            <Dropdown
              show={showBrandMenu}
              onToggle={(isOpen) => {
                setShowBrandMenu(isOpen);
                if (isOpen && brandInputRef.current) {
                  setTimeout(() => brandInputRef.current?.focus(), 100);
                }
              }}
            >
              <Dropdown.Toggle variant="none" className="dropdown-toggle">
                {selectedBrand ? selectedBrand.brand_name : "Seleccione una marca..."}
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu">
                <div className="dropdown-search">
                  <input
                    ref={brandInputRef}
                    type="text"
                    placeholder="Buscar marca..."
                    value={brandSearch}
                    onChange={(e) => setBrandSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedBrand(null);
                    setBrandSearch("");
                    setShowBrandMenu(false);
                  }}
                  style={{ opacity: 0.5, fontStyle: 'italic' }}
                >
                  Sin marca
                </Dropdown.Item>
                {isLoadingDropdowns ? (
                  <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando marcas...</Dropdown.Item>
                ) : filteredBrands.length === 0 ? (
                  <Dropdown.Item disabled>No se encontraron marcas</Dropdown.Item>
                ) : (
                  filteredBrands.map((brand) => (
                    <Dropdown.Item
                      key={brand.brand_id}
                      onClick={() => {
                        setSelectedBrand(brand);
                        setBrandSearch("");
                        setShowBrandMenu(false);
                      }}
                    >
                      {brand.brand_name}
                    </Dropdown.Item>
                  ))
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          </div> {/* end two-column-row */}
          {/* Category + Date (two-column row) */}
          <div className="two-column-row">
          <div className="form-field category-field">
            <div className="field-label">Categoría</div>
            <Dropdown
              show={showCategoryMenu}
              onToggle={(isOpen) => setShowCategoryMenu(isOpen)}
            >
              <Dropdown.Toggle variant="none" className="dropdown-toggle">
                {selectedCategory ? selectedCategory.category_name : "Seleccione categoría..."}
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu">
                <div className="dropdown-search">
                  <input
                    type="text"
                    placeholder="Buscar categoría..."
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedCategory(null);
                    setCategorySearch("");
                    setShowCategoryMenu(false);
                  }}
                  style={{ opacity: 0.5, fontStyle: 'italic' }}
                >
                  Sin categoría
                </Dropdown.Item>
                {isLoadingDropdowns ? (
                  <Dropdown.Item disabled><Spinner animation="border" size="sm" className="me-2" />Cargando categorías...</Dropdown.Item>
                ) : filteredCategories.length === 0 ? (
                  <Dropdown.Item disabled>No se encontraron categorías</Dropdown.Item>
                ) : (
                  filteredCategories.map((cat) => (
                    <Dropdown.Item
                      key={cat.expense_category_id}
                      onClick={() => {
                        setSelectedCategory(cat);
                        setCategorySearch("");
                        setShowCategoryMenu(false);
                      }}
                    >
                      {cat.category_name}
                    </Dropdown.Item>
                  ))
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Purchase Date */}
          <div className="form-field date-field">
            <div className="field-label">Fecha de compra</div>
            <Form.Control
              type="date"
              className="modern-input"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
            />
          </div>
          </div> {/* end two-column-row */}

          {/* Notes */}
          <div className="form-field notes-field">
            <div className="field-label">Notas</div>
            <Form.Control
              as="textarea"
              rows={2}
              className="modern-input"
              placeholder="Notas adicionales..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button variant="none" className="form-button-cancel" onClick={() => setShow(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button variant="none" className="form-button-create" onClick={handleUpdate} disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Guardando...
              </>
            ) : (
              "Guardar Cambios"
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditPurchase;